import {
  Box,
  ButtonProps,
  Flex,
  FlexProps,
  forwardRef,
  PortalManager,
  Tooltip as ChakraTooltip,
  TooltipProps as ChakraTooltipProps,
  useMergeRefs,
} from '@chakra-ui/react';
import { PencilIcon } from '@heroicons/react/24/outline';
import useSize from 'ahooks/es/useSize';
import {
  Children,
  cloneElement,
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  ActionButtonProps,
  FloatingActionButtons,
  FloatingButtonsContainer,
  TableRowFloatingButtonsContext,
} from '../floating-action-buttons';

type OverflowContainerProps = FlexProps & {
  /** When false, defers floating-button hover to an ancestor row container. */
  groupHover?: boolean;
};

export const OverflowContainer = ({ groupHover, ...props }: OverflowContainerProps) => {
  const isInTableRow = useContext(TableRowFloatingButtonsContext);
  const useGroupHover = groupHover ?? !isInTableRow;
  const flexProps = { gap: 3, align: 'center', maxW: '100%', w: '100%', ...props };

  if (useGroupHover) {
    return <FloatingButtonsContainer as={Flex} {...flexProps} />;
  }

  return <Flex {...flexProps} />;
};

interface TooltipProps extends Omit<ChakraTooltipProps, 'children'> {
  children: ReactElement;
}
const Tooltip = forwardRef<TooltipProps, 'div'>((props, propRef) => {
  const isInTableRow = useContext(TableRowFloatingButtonsContext);
  const [isOverflown, setIsOverflown] = useState(false);
  const textElementRef = useRef<HTMLParagraphElement>(null);
  const textContainerSize = useSize(textElementRef);
  const ref = useMergeRefs(textElementRef, propRef);

  /* Copy existing props of the children and add ref prop to it */
  const child = Children.only(props.children);
  const Component = cloneElement(child, { ref });

  /** Check whether the element is overflow or not */
  const compareSize = () => {
    const trigger = textElementRef.current;
    // A cell's padding box never overflows, so a Text it pads is measured instead.
    const paddedText = Array.from(trigger?.children ?? []).find(
      (child): child is HTMLElement =>
        child instanceof HTMLElement && child.dataset['component'] === 'text',
    );
    const element = paddedText ?? trigger;

    setIsOverflown(
      element
        ? element.offsetWidth < element.scrollWidth ||
            // HACK: Substract 1px to account for height errors in macos browsers
            element.offsetHeight < element.scrollHeight - 1
        : false,
    );
  };

  useEffect(compareSize, [props.label, textContainerSize]);

  /* On ComplyKit's overlay layer (--ck-z-overlay), not Chakra's portal layer
   * of 40, and appended to body rather than nested in a drawer's portal, so it
   * opens after (and above) a ComplyKit popover or menu it sits in. */
  const tooltip = (
    <PortalManager zIndex={50}>
      <ChakraTooltip
        isDisabled={!isOverflown}
        hasArrow
        portalProps={{ appendToParentPortal: false }}
        {...props}
      >
        {Component}
      </ChakraTooltip>
    </PortalManager>
  );

  if (!isInTableRow) {
    return tooltip;
  }

  return (
    <Box flex={1} minW={0} overflow="hidden">
      {tooltip}
    </Box>
  );
});

const EditTrigger = forwardRef<
  Omit<ButtonProps, 'onClick'> &
    Partial<Pick<ActionButtonProps, 'buttons'>> & {
      hideEditIcon?: boolean;
      reserveSpace?: boolean;
      tooltip?: string;
      /** Press-invoked: no event, and presses do not bubble (see actions/types.ts). */
      onClick?: () => void;
    },
  'button'
>((props, propRef) => {
  const { t } = useTranslation('ui');
  return (
    <FloatingActionButtons
      mr={2}
      ref={propRef}
      isAttached={false}
      reserveSpace={props.reserveSpace}
      background="transparent"
      buttons={[
        ...(props.hideEditIcon
          ? []
          : [
              {
                'aria-label': props.tooltip ?? t('overflowTooltip.edit'),
                tooltip: props.tooltip ?? t('overflowTooltip.edit'),
                icon: PencilIcon,
                onClick: () => props.onClick?.(),
              },
            ]),
        ...(props.buttons ?? []),
      ]}
    />
  );
});

OverflowContainer.Tooltip = Tooltip;
OverflowContainer.EditTrigger = EditTrigger;
